import { Button, EmptyState, IconSet, Panel } from '@postscript/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import LaunchpadProgressCard from 'components/launchpad/LaunchpadProgressCard';
import {
  LaunchpadGuideInfo,
  useLaunchpadGuides,
} from 'components/launchpad/useLaunchpadGuides';

import {
  LAUNCHPAD_DESCRIPTION,
  LAUNCHPAD_LABEL,
} from 'components/launchpad/constants';
import { logEvent, PRODUCT_AREAS } from 'utils/events';

interface LaunchpadPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const StyledCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const StyledViewAllRow = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  justify-content: center;
  padding: var(--spacing-1) 0;
  width: 100%;
  cursor: pointer;
`;

const StyledDivider = styled.div`
  flex: 1;
  height: 1px;
  background: var(--border-color-dim);
`;

const StyledEmptyState = styled(EmptyState)`
  padding: var(--spacing-10) 0;
`;

const LaunchpadPanel: React.FC<LaunchpadPanelProps> = ({ isOpen, onClose }) => {
  const [showAll, setShowAll] = useState(false);
  const { incompleteGuides, completedGuides, setActiveGuide } =
    useLaunchpadGuides();

  const closeReasonRef = useRef<'dismissed' | 'navigate_to_guide'>('dismissed');
  const previousIsOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      logEvent('launchpad panel opened', {
        location: 'nav menu',
        product_area: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      });
    } else if (!isOpen && previousIsOpenRef.current) {
      logEvent('launchpad panel closed', {
        location: 'nav menu',
        reason: closeReasonRef.current,
        product_area: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      });
      closeReasonRef.current = 'dismissed';
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);

  const handleGuideClick = useCallback(
    (info: LaunchpadGuideInfo) => {
      closeReasonRef.current = 'navigate_to_guide';
      onClose();
      setActiveGuide(info.guide);
    },
    [onClose, setActiveGuide],
  );

  const renderGuideCard = (info: LaunchpadGuideInfo) => (
    <LaunchpadProgressCard
      key={info.guide.key}
      title={info.guide.title}
      subtitle={info.subtitle}
      icon={IconSet[info.guide.icon]}
      completedSteps={info.completedSteps}
      totalSteps={info.totalSteps}
      isComplete={info.isComplete}
      onClick={() => handleGuideClick(info)}
    />
  );

  const hasCompleted = completedGuides.length > 0;
  const hasGuides = incompleteGuides.length > 0 || hasCompleted;

  return (
    <Panel
      isOpen={isOpen}
      onCancel={onClose}
      placement="drawer"
      dismissOnBackdropClick
      visibleBackdrop={false}
      width={444}
    >
      <Panel.Header
        description={LAUNCHPAD_DESCRIPTION}
        onCancel={onClose}
        icon={IconSet.Compass}
        iconBackgroundColor="var(--blue-1)"
      >
        {LAUNCHPAD_LABEL}
      </Panel.Header>
      <Panel.Body>
        {hasGuides ? (
          <StyledCardList>
            {incompleteGuides.map(renderGuideCard)}
            {showAll && completedGuides.map(renderGuideCard)}
            {hasCompleted && (
              <StyledViewAllRow onClick={() => setShowAll((prev) => !prev)}>
                <StyledDivider />
                <Button
                  variant="plain"
                  size="small"
                  icon={showAll ? IconSet.ChevronUp : IconSet.ChevronDown}
                  iconPosition="right"
                >
                  View All
                </Button>
                <StyledDivider />
              </StyledViewAllRow>
            )}
          </StyledCardList>
        ) : (
          <StyledEmptyState
            description="You're up to date on Postscript features!"
            size="small"
            primaryAction={{
              'data-cy': 'launchpad-panel-close',
              text: 'Close',
              onClick: onClose,
            }}
          />
        )}
      </Panel.Body>
    </Panel>
  );
};

export default LaunchpadPanel;
